import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Grid,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Chip,
  CircularProgress,
  Avatar,
  Tooltip,
  Alert,
} from '@mui/material';
import TableToolbar from '../components/TableToolbar';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    postal_code: '',
    password: '',
    status: '1',
  });

  // Get token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      // You might want to redirect to login page here
      return null;
    }
    return token;
  };

  // Common axios configuration with auth header
  const getAxiosConfig = () => {
    const token = getAuthToken();
    if (!token) return null;

    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  useEffect(() => {
    fetchCompanies();
  }, [currentPage, filters]);

  const fetchCompanies = async () => {
    const config = getAxiosConfig();
    if (!config) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...filters
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/v1/companies-list?${params}`,
        config
      );
      
      if (response.data && response.data.status && response.data.data) {
        setCompanies(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.total_pages || 1);
      } else {
        setCompanies([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error(error.response?.data?.message || 'Error fetching companies');
      }
      setCompanies([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      console.log('Opening dialog for editing company:', company);
      setSelectedCompany(company);
      setFormData({
        company_name: company.company_name || '',
        first_name: company.first_name || '',
        last_name: company.last_name || '',
        email: company.email || '',
        phone: company.phone || '',
        postal_code: company.postal_code || '',
        password: '',
        status: company.status?.toString() || '1',
      });
    } else {
      console.log('Opening dialog for new company');
      setSelectedCompany(null);
      setFormData({
        company_name: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        postal_code: '',
        password: '',
        status: '1',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCompany(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, logo: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const config = getAxiosConfig();
    if (!config) return;

    const requestData = {
      company_name: formData.company_name || '',
      first_name: formData.first_name || '',
      last_name: formData.last_name || '',
      email: formData.email || '',
      phone: formData.phone || '',
      postal_code: formData.postal_code || '',
      status: formData.status || '',
      password: formData.password || ''
    };

    try {
      let response;
      if (selectedCompany) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/v1/companies-list/${selectedCompany.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/v1/companies-list`,
          requestData,
          config
        );
      }
      
      if (response.data.status === true) {
        toast.success(response.data.message || (selectedCompany ? 'Company updated successfully' : 'Company created successfully'));
        handleCloseDialog();
        fetchCompanies();
      } else {
        toast.error(response.data.message || 'Error saving company');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error(error.response?.data?.message || 'Error saving company');
      }
    }
  };

  const handleDelete = async (id) => {
    const config = getAxiosConfig();
    if (!config) return;

    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/v1/companies-list/${id}/status`,
          { status: 0 },
          config
        );
        
        if (response.data.status === true) {
          toast.success(response.data.message || 'Company deactivated successfully');
        } else {
          toast.error(response.data.message || 'Error deactivating company');
        }
        fetchCompanies();
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          // You might want to redirect to login page here
        } else {
          toast.error(error.response?.data?.message || 'Error deactivating company');
        }
      }
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const config = getAxiosConfig();
    if (!config) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/companies-list/${id}/status`,
        { status: currentStatus === 1 ? 0 : 1 },
        config
      );
      toast.success(`Company ${currentStatus === 1 ? 'deactivated' : 'activated'} successfully`);
      fetchCompanies();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error('Error updating company status');
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <TableToolbar title="Company Management" />

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name or email"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
                startAdornment={<FilterIcon />}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white' }}>S.No</TableCell>
              <TableCell sx={{ color: 'white' }}>Company Name</TableCell>
              <TableCell sx={{ color: 'white' }}>Contact</TableCell>
              <TableCell sx={{ color: 'white' }}>Email</TableCell>
              <TableCell sx={{ color: 'white' }}>Phone</TableCell>
              <TableCell sx={{ color: 'white' }}>Logo</TableCell>
              <TableCell sx={{ color: 'white' }}>Status</TableCell>
              <TableCell sx={{ color: 'white' }}>Plan</TableCell>
              <TableCell sx={{ color: 'white' }}>Created At</TableCell>
              <TableCell sx={{ color: 'white' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Alert severity="info">No companies found</Alert>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company, index) => (
                <TableRow key={company.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{company.company_name}</TableCell>
                  <TableCell>
                    {company.first_name} {company.last_name}
                  </TableCell>
                  <TableCell>{company.email}</TableCell>
                  <TableCell>{company.phone}</TableCell>
                  <TableCell>
                    <Avatar
                      src={company.logo}
                      alt={company.company_name}
                      sx={{ width: 40, height: 40 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={company.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                      label={company.status === 1 ? 'Active' : 'Inactive'}
                      color={company.status === 1 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{company.plan_name || '-'}</TableCell>
                  <TableCell>
                    {new Date(company.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(company)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(company.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                  
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {companies.length} of {totalItems} companies
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCompany ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!selectedCompany}
                helperText={selectedCompany ? "Leave blank to keep current password" : "Enter password"}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedCompany ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyList; 